import type { NodeDataPool, NodeDisk, NodeStorageTier, StorageTier } from '../api/types'
import { DASH, size, sizeNumber, sizeUnit, sizeUsage } from '../ui/format'

// 디스크 단계·종류·판정을 화면의 낱말로 옮기는 층(A6-12). 순수 함수만 둔다.
// 단계는 넷이고 순서가 있다: 기본은 빠른 것부터, 부팅 디스크가 마지막이다.

export const TIERS: StorageTier[] = ['m2', 'ssd', 'hdd', 'boot']

const TIER: Record<string, string> = { m2: 'M.2', ssd: 'SSD', hdd: 'HDD', boot: '부팅' }
/** 단계와 디스크 종류는 같은 낱말을 쓴다 — 종류가 곧 그 종류의 풀이다. */
export function tierWord(tier: string | null | undefined): string {
  return tier ? (TIER[tier] ?? DASH) : DASH
}

const EXCLUDED: Record<string, string> = {
  bootdisk: '부팅 디스크',
  in_use: '사용 중',
  unhealthy: '건강하지 않음',
  unknown_type: '종류 모름',
  unknown_health: '건강 모름',
  unknown_name: '이름 모름',
  busy: '비우지 못함',
  install_media: '설치 매체',
  removable: '이동식',
  hw_raid: '하드웨어 RAID 볼륨',
}
export function excludedReasonWord(reason: string | null | undefined): string {
  return reason ? (EXCLUDED[reason] ?? DASH) : DASH
}

/** 디스크 건강은 PVE 의 낱말(PASSED·OK·FAILED…)이라 셋으로 줄인다. */
export function diskHealthWord(health: string | null | undefined): string {
  if (!health) return DASH
  const h = health.toUpperCase()
  if (h === 'PASSED' || h === 'OK') return '정상'
  if (h === 'UNKNOWN') return DASH
  return '이상'
}

/** 디스크 한 장의 크기. 풀·단계의 수와 같은 사다리(1024 기수)라 제조사 라벨 4TB 는 `3.6TB` 다 — 같은 화면에서 견줄 수 있게. */
export function diskSizeText(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return DASH
  return size(bytes / 2 ** 30)
}

/**
 * 디스크 한 줄: 종류 · 크기 · 건강 · 풀 또는 제외 사유, 첫 부팅이 지운 것이 있으면 그것. 값이 없으면 대시다.
 * LVM-thin 풀의 멤버는 하드웨어 RAID 볼륨이라 그렇게 말한다(#463) — 풀 종류는 pools 에만 있다.
 */
export function diskText(d: NodeDisk, pools: NodeDataPool[] = []): string {
  const thin = d.pool != null && pools.some((p) => p.pool_name === d.pool && p.kind === 'lvmthin')
  const pool = thin ? `풀 ${d.pool} · 하드웨어 RAID 볼륨 · LVM 으로 임대` : `풀 ${d.pool}`
  const place = d.pool ? pool : d.excluded_reason ? `제외 · ${excludedReasonWord(d.excluded_reason)}` : d.used_by ? `사용 중 · ${d.used_by}` : '정해지지 않음'
  const cleared = d.cleared ? ` · 지움 ${d.cleared}` : ''
  return `${tierWord(d.class)} · ${diskSizeText(d.size_bytes)} · ${diskHealthWord(d.health)} · ${place}${cleared}`
}

/**
 * 단계 한 줄: `할당 / 전체`, 전체를 재지 못했으면 대시. 노드에 없는 단계는 `없음`.
 * 전체가 단위를 정하고 할당·예약도 그 단위다 — 전체를 모르면 할당이 정한다.
 */
export function tierText(t: NodeStorageTier): string {
  if (!t.present) return '없음'
  const unit = sizeUnit(t.total_gb ?? t.allocated_gb)
  const at = (gib: number | null | undefined) => (gib == null ? DASH : `${sizeNumber(gib, unit)}${unit}`)
  const reserve = t.reserve_gb > 0 ? ` · 예약 ${at(t.reserve_gb)}` : ''
  return `${t.storage} · 할당 ${sizeUsage(t.allocated_gb, t.total_gb)}${reserve}`
}

/** 순서 안에서 한 칸 옮긴 새 배열. 끝을 넘으면 그대로다. */
export function moveTier(order: StorageTier[], index: number, delta: -1 | 1): StorageTier[] {
  const to = index + delta
  if (index < 0 || index >= order.length || to < 0 || to >= order.length) return order
  const next = [...order]
  const [item] = next.splice(index, 1)
  next.splice(to, 0, item!)
  return next
}
